import { memo, type PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';

import { EditCanvasDropzoneProvider } from '~/components/PrivateDropzone/EditCanvasDropzoneContext';
import { PrivateUploadProvider } from '~/components/PrivateDropzone/PrivateUploadProvider';
import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';
import { useCanUploadAssets } from '~/swr-hooks/members/useCanUploadAssets';

export type PrivateDropzoneProps = PropsWithChildren<{
  disableDrop?: boolean;
}>;

export const PrivateDropzone = memo(({ children, disableDrop = false }: PrivateDropzoneProps) => {
  const { canUploadAssets } = useCanUploadAssets();
  const { similarToImageSearchAvailable } = usePrivateSearchUIHelpers();
  const { isSearchCommandOpen } = usePrivateSearchContext();
  const currentBoardId = useURLBoardIdSelector();
  const currentBoardTitle = useSelector(centralizedBoardTitleSelector);

  return (
    <EditCanvasDropzoneProvider>
      <PrivateUploadProvider
        currentBoardTitle={currentBoardTitle}
        disableDrag={!canUploadAssets || disableDrop}
        disabled={isSearchCommandOpen && similarToImageSearchAvailable}
        supportsDirectUpload={!!currentBoardId && canUploadAssets}
        supportsSimilarToImageSearch={similarToImageSearchAvailable}
      >
        {children}
      </PrivateUploadProvider>
    </EditCanvasDropzoneProvider>
  );
});

PrivateDropzone.displayName = 'PrivateDropzone';
